import React from 'react';
import { Box, Typography } from '@material-ui/core';
import { MODES } from '../../../../utils/constant';
import { RangeSlider } from '../../../../components/RangeSlider';
import { stopThumbs } from '../util';
import { getAirUnit } from '../../../../utils/utility';
import { useMobileStyles } from '../styles';
import I18n from '../../../../localization';

const SmartModeControls = ({ iqAirDetails, IQAirPoints, availableModes, marksArray, range, onComfortRangeChange, isDarkMode }) => {
  const classes = useMobileStyles();
  const isF = getAirUnit() === "F";
  const autoStep = 0.5;
  const autoSpan = isF ? 4 : 2;

  const isAutoMode = Array.isArray(availableModes) && availableModes.includes(MODES.HEAT) && availableModes.includes(MODES.COOL);
  const isHeatOnlyMode = Array.isArray(availableModes) && availableModes.includes(MODES.HEAT) && !availableModes.includes(MODES.COOL);
  const isCoolOnlyMode = Array.isArray(availableModes) && availableModes.includes(MODES.COOL) && !availableModes.includes(MODES.HEAT);

  const heatSetpoint = Number(IQAirPoints?.heat_setpoint);
  const coolSetpoint = Number(IQAirPoints?.cool_setpoint);

  const buildAllowedValues = (start, end) => {
    if (!Number.isFinite(start) || !Number.isFinite(end) || end < start) return [];
    const count = Math.round((end - start) / autoStep);
    return Array.from({ length: count + 1 }, (_, idx) => Number((start + idx * autoStep).toFixed(1)));
  };

  const leftAllowed = Number.isFinite(heatSetpoint)
    ? buildAllowedValues(heatSetpoint - autoSpan, heatSetpoint)
    : [];
  const rightAllowed = Number.isFinite(coolSetpoint)
    ? buildAllowedValues(coolSetpoint, coolSetpoint + autoSpan)
    : [];

  const leftTicks = leftAllowed.reduce(
    (a, v, i) => ({
      ...a,
      [v]: `${i % 2 === 1 ? "" : v}`,
    }),
    {}
  );
  const rightTicks = rightAllowed.reduce(
    (acc, value, index) => ({
      ...acc,
      [value]: `${index % 2 === 1 ? "" : value}`,
    }),
    {}
  );

  const handleHeatRangeChange = ([left, right], index) => {
    const center = IQAirPoints?.heat_setpoint;
    if (!Number.isFinite(center)) return;
    if (stopThumbs(left, right, center, index, isF)) return;
    onComfortRangeChange('heat_setpoint', [left, right], index);
  };

  const handleCoolRangeChange = ([left, right], index) => {
    const center = IQAirPoints?.cool_setpoint;
    if (!Number.isFinite(center)) return;
    if (stopThumbs(left, right, center, index, isF)) return;
    onComfortRangeChange('cool_setpoint', [left, right], index);
  };

  const snapToAllowed = (value, allowed) => {
    if (!Array.isArray(allowed) || allowed.length === 0 || !Number.isFinite(value)) {
      return value;
    }

    return allowed.reduce((best, current) => (
      Math.abs(current - value) < Math.abs(best - value) ? current : best
    ), allowed[0]);
  };

  const getAutoSliderValue = (setpointType, boundIndex, fallbackValue, allowed) => {
    const currentValue = Number(range?.[setpointType]?.[boundIndex]);
    if (!Number.isFinite(currentValue)) {
      return fallbackValue;
    }
    return snapToAllowed(currentValue, allowed);
  };

  const autoLeftValue = getAutoSliderValue('heat_setpoint', 0, heatSetpoint, leftAllowed);
  const autoRightValue = getAutoSliderValue('cool_setpoint', 1, coolSetpoint, rightAllowed);
  const heatComfortValue = Number.isFinite(autoLeftValue)
    ? autoLeftValue
    : Number(range?.heat_setpoint?.[0]);
  const coolComfortValue = Number.isFinite(autoRightValue)
    ? autoRightValue
    : Number(range?.cool_setpoint?.[1]);
  const heatRangeLeft = Number(range?.heat_setpoint?.[0]);
  const heatRangeRight = Number(range?.heat_setpoint?.[1]);
  const coolRangeLeft = Number(range?.cool_setpoint?.[0]);
  const coolRangeRight = Number(range?.cool_setpoint?.[1]);

  const shouldShowAutoValues = isAutoMode && Number.isFinite(heatComfortValue) && Number.isFinite(coolComfortValue);
  const shouldShowHeatOnlyValues = isHeatOnlyMode && Number.isFinite(heatRangeLeft) && Number.isFinite(heatRangeRight);
  const shouldShowCoolOnlyValues = isCoolOnlyMode && Number.isFinite(coolRangeLeft) && Number.isFinite(coolRangeRight);

  const formatTempValue = (value) => {
    if (!Number.isFinite(value)) return '--';
    const rounded = Number(value.toFixed(1));
    return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1);
  };

  const extractSliderValue = (valueOrArray) => {
    if (Array.isArray(valueOrArray)) {
      return Number(valueOrArray[0]);
    }
    return Number(valueOrArray);
  };

  const handleHeatAutoChange = (valueOrArray) => {
    if (!isAutoMode) return;

    const nextLeft = snapToAllowed(extractSliderValue(valueOrArray), leftAllowed);
    const currentHeatUpper = Number(range?.heat_setpoint?.[1]);

    onComfortRangeChange(
      'heat_setpoint',
      [nextLeft, Number.isFinite(currentHeatUpper) ? currentHeatUpper : heatSetpoint],
      0
    );
  };

  const handleCoolAutoChange = (valueOrArray) => {
    if (!isAutoMode) return;

    const nextRight = snapToAllowed(extractSliderValue(valueOrArray), rightAllowed);
    const currentCoolLower = Number(range?.cool_setpoint?.[0]);

    onComfortRangeChange(
      'cool_setpoint',
      [Number.isFinite(currentCoolLower) ? currentCoolLower : coolSetpoint, nextRight],
      1
    );
  };

  return (
    <Box className={classes.sliderContainer}>
      <Box style={{ display: 'flex', justifyContent: 'space-between', alignItems:'baseline' }}>
        <Typography type='label' className={classes.cardInnerLabel}>
          {I18n.t("mobile.energyMode.comfortLimit")}
        </Typography>
        {shouldShowAutoValues ? (
          <Box className={classes.setpointValueContainer}>
            <Typography
              type='label'
              className={`${classes.setpointValueText} ${isDarkMode ? classes.heatSetpointDark : classes.heatSetpointLight}`}
            >
              {formatTempValue(heatComfortValue)}°{getAirUnit()}
            </Typography>
            <Typography type='label' className={classes.setpointSeparator}>
              -
            </Typography>
            <Typography
              type='label'
              className={`${classes.setpointValueText} ${isDarkMode ? classes.coolSetpointDark : classes.coolSetpointLight}`}
            >
              {formatTempValue(coolComfortValue)}°{getAirUnit()}
            </Typography>
          </Box>
        ) : shouldShowHeatOnlyValues ? (
          <Box className={classes.setpointValueContainer}>
            <Typography
              type='label'
              className={`${classes.setpointValueText} ${isDarkMode ? classes.heatSetpointDark : classes.heatSetpointLight}`}
            >
              {formatTempValue(heatRangeLeft)}°{getAirUnit()}
            </Typography>
            <Typography type='label' className={classes.setpointSeparator}>
              -
            </Typography>
            <Typography
              type='label'
              className={`${classes.setpointValueText} ${isDarkMode ? classes.heatSetpointDark : classes.heatSetpointLight}`}
            >
              {formatTempValue(heatRangeRight)}°{getAirUnit()}
            </Typography>
          </Box>
        ) : shouldShowCoolOnlyValues ? (
          <Box className={classes.setpointValueContainer}>
            <Typography
              type='label'
              className={`${classes.setpointValueText} ${isDarkMode ? classes.coolSetpointDark : classes.coolSetpointLight}`}
            >
              {formatTempValue(coolRangeLeft)}°{getAirUnit()}
            </Typography>
            <Typography type='label' className={classes.setpointSeparator}>
              -
            </Typography>
            <Typography
              type='label'
              className={`${classes.setpointValueText} ${isDarkMode ? classes.coolSetpointDark : classes.coolSetpointLight}`}
            >
              {formatTempValue(coolRangeRight)}°{getAirUnit()}
            </Typography>
          </Box>
        ) : (
          <Typography className={[classes.sliderHeader, classes.unit]}>
            °{getAirUnit()}
          </Typography>
        )}
      </Box>

      
      {isAutoMode && leftAllowed.length > 0 && rightAllowed.length > 0 && Number.isFinite(autoLeftValue) && Number.isFinite(autoRightValue) ? <>
        <Box className={classes.sliderWrapper}>
          <Box className={classes.autoSetpointLabels}>
            <Box className={classes.autoComfortSegment}>
              <div className={`${classes.setpointLabel} ${classes.autoHeatSetpointLabel}`}>
                {I18n.t("mobile.energyMode.heatsetpoint")}
                <div className={`${classes.setpointArrow} ${classes.autoHeatSetpointArrow}`} />
              </div>
            </Box>

            <Box className={classes.autoLabelGap} />

            <Box className={classes.autoComfortSegment}>
              <div className={`${classes.setpointLabel} ${classes.autoCoolSetpointLabel}`}>
                {I18n.t("mobile.energyMode.coolsetpoint")}
                <div className={`${classes.setpointArrow} ${classes.autoCoolSetpointArrow}`} />
              </div>
            </Box>
          </Box>

          <Box className={classes.autoComfortRow}>
            <Box className={classes.autoComfortSegment}>
              <RangeSlider
                isWeb={false}
                value={[autoLeftValue]}
                marks={leftAllowed}
                ticks={leftTicks}
                min={leftAllowed[0]}
                max={leftAllowed[leftAllowed.length - 1]}
                step={autoStep}
                withTracks={true}
                onChange={handleHeatAutoChange}
                onAfterChange={handleHeatAutoChange}
                className={`${classes.autoHeatSlider} ${isDarkMode ? 'dark-theme' : ''}`}
              />
            </Box>

            <Box className={classes.autoComfortGap} />

            <Box className={classes.autoComfortSegment}>
              <RangeSlider
                isWeb={false}
                value={[autoRightValue]}
                marks={rightAllowed}
                ticks={rightTicks}
                min={rightAllowed[0]}
                max={rightAllowed[rightAllowed.length - 1]}
                step={autoStep}
                withTracks={true}
                onChange={handleCoolAutoChange}
                onAfterChange={handleCoolAutoChange}
                className={`${classes.autoCoolSlider} ${isDarkMode ? 'dark-theme' : ''}`}
              />
            </Box>
          </Box>
        </Box>
      </> : null}



      { isHeatOnlyMode && 
       Array.isArray(marksArray.heat_setpoint) && Array.isArray(range.heat_setpoint) && (
        <Box className={classes.sliderWrapper}>
          <div className={classes.setpointLabel}>
            {I18n.t("mobile.energyMode.heatsetpoint")}
            <div className={classes.setpointArrow} />
          </div>

          <RangeSlider   
            isWeb={false}               
            value={range.heat_setpoint}
            marks={marksArray.heat_setpoint}
            ticks={marksArray.heat_setpoint.reduce(
              (a, v, i) => ({
                ...a,
                [v]: `${i % 2 === 1 ? "" : v}`,
              }),
              {}
            )}
            min={marksArray.heat_setpoint[0]}
            max={marksArray.heat_setpoint.at(-1)}
            step={isF ? 1 : 0.5}
            withTracks={true}
            pearling={false}
            minDistance={isF ? 1 : 0.5}
            onChange={handleHeatRangeChange}
            onAfterChange={handleHeatRangeChange}
            className={`${
              range.heat_setpoint[1] === marksArray.heat_setpoint.at(-1) ? 'last_point' : ''
            } ${isDarkMode ? 'dark-theme' : ''} orange`}
          />
        </Box>
      )}

      {isCoolOnlyMode && 
       Array.isArray(marksArray.cool_setpoint) && Array.isArray(range.cool_setpoint) && (
        <Box className={classes.sliderWrapper}>
          <div className={classes.setpointLabel}>
            {I18n.t("mobile.energyMode.coolsetpoint")}
            <div className={classes.setpointArrow} />
          </div>

          <RangeSlider
            isWeb={false}
            value={range.cool_setpoint}
            marks={marksArray.cool_setpoint}
            ticks={marksArray.cool_setpoint.reduce(
              (a, v, i) => ({
                ...a,
                [v]: `${i % 2 === 1 ? "" : v}`,
              }),
              {}
            )}
            min={marksArray.cool_setpoint[0]}
            max={marksArray.cool_setpoint.at(-1)}
            step={isF ? 1 : 0.5}
            withTracks={true}
            pearling={false}
            minDistance={isF ? 1 : 0.5}
            onChange={handleCoolRangeChange}
            onAfterChange={handleCoolRangeChange}
            className={`${
              range.cool_setpoint[1] === marksArray.cool_setpoint.at(-1) ? 'last_point' : ''
            } ${isDarkMode ? 'dark-theme' : ''}`}
          />
        </Box>
      )}
      
      <Box className={classes.aiCoolBox}>
        {I18n.t("mobile.energyMode.shiftHVACRuntime")}
      </Box>
    </Box>
  );
};

export default SmartModeControls;
